import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import API_URL from "../apiConfig";
import "../styles/FeeModules.css";

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function formatCurrency(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function parseDateSafe(s) {
  if (!s) return null;
  if (s instanceof Date) return s;
  if (typeof s !== "string") return new Date(s);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s + "T00:00:00");
  const t = Date.parse(s);
  return Number.isNaN(t) ? null : new Date(t);
}
function normalizeName(n = "") { return String(n || "").replace(/\s+/g, " ").trim(); }
function normalizeRoll(r = "") { return String(r || "").trim(); }
function looksLikeRefund(r) {
  if (!r) return false;
  const status = String(r.status || "").toLowerCase();
  const mode = String(r.mode || "").toLowerCase();
  if (status.includes("refund") || mode.includes("refund")) return true;
  const amt = safeNum(r.amount ?? r.amount_paid ?? r.scholarship_amount ?? 0);
  return amt < 0;
}

export default function FeeReports({ token }) {
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [feeRows, setFeeRows] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState("");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [compact, setCompact] = useState(false);

  const printRootRef = useRef();

  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  // Compute display name for the currently selected batch (used inside printable root)
  const batchDisplayName = useMemo(() => {
    if (!selectedBatch) return "All Batches";
    const b = batches.find(bb => String(bb.id) === String(selectedBatch));
    return b ? b.name : String(selectedBatch);
  }, [selectedBatch, batches]);

  // Global print / PDF style injection (scoped)
  useEffect(() => {
    const id = "fee-report-global-print-style";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.innerHTML = `
      @media print {
        :not(#fee-report-print-root):not(#fee-report-print-root *) { display: none !important; }
        #fee-report-print-root { display:block !important; width:100% !important; max-width:none !important; margin:0 !important; padding:0 !important; }
        #fee-report-print-root thead { display: table-header-group; }
        #fee-report-print-root tfoot { display: table-footer-group; }
        #fee-report-print-root tr { page-break-inside: avoid; }
        html, body { background: #fff !important; -webkit-print-color-adjust: exact; color-adjust: exact; }
        #fee-report-print-root .controls, #fee-report-print-root .actions, #fee-report-print-root .compactToggle, #fee-report-print-root .fee-action-btn { display: none !important; }
      }
      #fee-report-print-root .pdf-batch-heading { font-size: 16px; font-weight: 800; color: #0b3b5b; margin-bottom: 12px; display:block; }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  // Scoped compact styles to help html2canvas capture a compact layout
  useEffect(() => {
    const id = "fee-report-local-compact-style";
    if (document.getElementById(id)) return;
    const styleEl = document.createElement("style");
    styleEl.id = id;
    styleEl.innerHTML = `
      #fee-report-print-root table { font-size: ${compact ? 10 : 11}px !important; }
      #fee-report-print-root th, #fee-report-print-root td { padding: 6px 8px !important; }
      #fee-report-print-root .fee-category-col { width: 56px !important; max-width:56px !important; text-align:center !important; padding-left:4px !important; padding-right:4px !important; }
      #fee-report-print-root td.numeric { white-space: nowrap !important; text-align: right; }
      #fee-report-print-root .student-name-cell { white-space: normal !important; word-break: break-word !important; max-width: 260px !important; }
      #fee-report-print-root .kpi-card, #fee-report-print-root .summary, #fee-report-print-root .category-kpi { font-size: 11px !important; }
      #fee-report-print-root .pdf-batch-heading { font-size: 16px !important; font-weight: 800 !important; color: #0b3b5b !important; margin-bottom: 12px !important; display:block !important; }
      #fee-report-print-root .category-grid { display: grid !important; grid-template-columns: repeat(auto-fit, minmax(220px, 360px)) !important; gap: 12px !important; justify-content: start !important; }
      #fee-report-print-root .category-kpi { background: #fff; border: 1px solid #eef3f8; border-radius: 8px; padding: 12px; display:flex; justify-content:space-between; align-items:center; max-width: 360px; }
    `;
    document.head.appendChild(styleEl);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [compact]);

  // --- Data loaders ---
  const loadBatches = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/batches`, { headers: authHeaders });
      if (!r.ok) throw new Error("Failed to load batches");
      const data = await r.json();
      const arr = Array.isArray(data) ? data : data?.data ?? [];
      const normalized = arr.map((b) => ({
        id: b.id ?? b.batch_id ?? String(b.name ?? ""),
        name: b.name ?? b.batch_name ?? String(b.id ?? ""),
        annual_fee: safeNum(b.annual_fee ?? b.annualFee ?? 0),
        raw: b,
      }));
      setBatches(normalized);
      setSelectedBatch((prev) => (prev ? prev : (normalized.length ? String(normalized[0].id) : "")));
    } catch (err) {
      console.error("loadBatches", err);
      setBatches([]);
      setError("Failed to load batches.");
    }
  }, [authHeaders]);

  const loadStudents = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API_URL}/api/students`, { headers: authHeaders });
      if (!r.ok) throw new Error("Failed to load students");
      const data = await r.json();
      setStudents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("loadStudents", err);
      setStudents([]);
      setError("Failed to load students.");
    }
  }, [authHeaders, token]);

  const loadFeeRows = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/fees/all-student-fees`, { headers: authHeaders });
      if (!r.ok) throw new Error("Failed to load fees");
      const data = await r.json();
      if (!Array.isArray(data)) {
        setFeeRows([]);
        setError("Unexpected fees data");
        return;
      }
      const normalized = data.map((f) => ({
        ...f,
        fee_id: f.fee_id ?? f.id ?? null,
        student_id: f.student_id ?? null,
        batch_id: f.batch_id ?? f.batch ?? null,
        student_name: f.student_name ?? f.name ?? "",
        roll_no: f.roll_no ?? f.roll ?? "",
        course_year: f.course_year !== undefined && f.course_year !== null ? Math.max(1, Math.min(3, Number(f.course_year))) : 1,
        amount: safeNum(f.amount),
        amount_paid: safeNum(f.amount_paid),
        scholarship_amount: safeNum(f.scholarship_amount),
        payment_date_parsed: parseDateSafe(f.payment_date || f.paymentDate || f.created_at),
        annual_fee: safeNum(f.annual_fee ?? f.annualFee ?? 0),
        mode: f.mode ?? "",
        status: f.status ?? "",
        raw: f,
      }));
      setFeeRows(normalized);
    } catch (err) {
      console.error("loadFeeRows", err);
      setFeeRows([]);
      setError("Failed to load fee records.");
    }
  }, [authHeaders]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        await Promise.all([loadBatches(), loadStudents(), loadFeeRows()]);
      } catch (e) {
        console.error(e);
        setError("Failed to load initial data.");
      } finally {
        setLoading(false);
      }
    })();
  }, [loadBatches, loadStudents, loadFeeRows]);

  // --- Aggregation helpers (unchanged) ---
  const batchAnnual = useMemo(() => {
    const m = {};
    batches.forEach((b) => { m[String(b.id)] = safeNum(b.annual_fee || 0); });
    return m;
  }, [batches]);

  const studentById = useMemo(() => {
    const m = {};
    students.forEach((s) => {
      const id = s.id ?? s.student_id;
      if (id !== undefined && id !== null) m[String(id)] = s;
    });
    return m;
  }, [students]);

  const resolveStudentForRow = useCallback((row) => {
    if (!row) return null;
    if (row.student_id && studentById[String(row.student_id)]) return studentById[String(row.student_id)];
    if (row.email) {
      const found = students.find((st) => (st.email || "").toLowerCase() === String(row.email || "").toLowerCase());
      if (found) return found;
    }
    const nm = normalizeName(row.student_name || row.name || "");
    if (nm) {
      const batchVal = String(row.session_year ?? row.batch_id ?? row.batch ?? "");
      const found = students.find((st) => normalizeName(st.name || st.student_name || "") === nm && String(st.session_year ?? st.batch_id ?? "") === batchVal);
      if (found) return found;
    }
    return null;
  }, [students, studentById]);

  const studentsAggregated = useMemo(() => {
    let rows = selectedBatch
      ? feeRows.filter((r) => {
        const bid = r.batch_id ?? r.raw?.batch_id ?? r.raw?.batch ?? r.session_year ?? r.raw?.session_year;
        return String(bid) === String(selectedBatch);
      })
      : feeRows.slice();

    const q = String(search || "").trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const name = (r.student_name || r.name || "").toString().toLowerCase();
        const roll = (r.roll_no || r.roll || "").toString().toLowerCase();
        const cat = (r.category || "").toString().toLowerCase();
        return name.includes(q) || roll.includes(q) || cat.includes(q);
      });
    }

    const groups = new Map();
    rows.forEach((r) => {
      let key = null;
      if (r.student_id) key = `SID:${r.student_id}`;
      else {
        const resolved = resolveStudentForRow(r);
        if (resolved && (resolved.id ?? resolved.student_id)) key = `SID:${resolved.id ?? resolved.student_id}`;
      }
      if (!key) {
        const nm = normalizeName(r.student_name || r.name || "");
        const rl = normalizeRoll(r.roll_no || r.roll || "");
        const batchVal = String(r.batch_id ?? r.session_year ?? r.batch ?? "");
        key = nm || rl ? `NAME:${nm}#ROLL:${rl}#BATCH:${batchVal}` : `ROW:${String(r.fee_id ?? Math.random())}`;
      }
      if (!groups.has(key)) groups.set(key, { key, rows: [] });
      groups.get(key).rows.push(r);
    });

    const out = [];
    const clampYear = (y) => { const n = Number(y) || 1; return Math.min(3, Math.max(1, n)); };

    for (const [, g] of groups) {
      const rep = g.rows.find((x) => x.student_id) || g.rows[0];
      const resolved = resolveStudentForRow(rep);
      const displayName = resolved ? (resolved.name || resolved.student_name) : (rep.student_name || rep.name || "Unknown");
      const displayCategory = resolved ? resolved.category : rep.category || "N/A";

      const annualCandidates = { 1: [], 2: [], 3: [] };
      g.rows.forEach((r) => {
        const candRow = safeNum(r.annual_fee || 0);
        const candBatch = safeNum(batchAnnual[String(r.batch_id ?? r.raw?.batch_id)] || 0);
        const candidate = candRow > 0 ? candRow : candBatch;
        const y = clampYear(r.course_year ?? r.raw?.course_year);
        if (candidate > 0) annualCandidates[y].push(candidate);
      });
      const annual = {
        1: annualCandidates[1].length ? Math.max(...annualCandidates[1]) : 0,
        2: annualCandidates[2].length ? Math.max(...annualCandidates[2]) : 0,
        3: annualCandidates[3].length ? Math.max(...annualCandidates[3]) : 0,
      };

      const perYear = { 1: {}, 2: {}, 3: {} };
      let totalReceived = 0, totalCarryIn = 0, totalApplied = 0, totalPending = 0, totalExcess = 0, totalRefunded = 0;

      for (let y = 1; y <= 3; y++) {
        const rowsY = g.rows.filter((rr) => clampYear(rr.course_year) === y);

        const tuitionPaid = rowsY.reduce((s, rr) => {
          const isCarry = String(rr.mode || "").toLowerCase().includes("carryforward");
          if (!isCarry && rr.status && String(rr.status).toLowerCase() === "paid") return s + safeNum(rr.amount_paid);
          return s;
        }, 0);

        const carryIn = rowsY.reduce((s, rr) => {
          const isCarry = String(rr.mode || "").toLowerCase().includes("carryforward");
          if (isCarry && rr.status && String(rr.status).toLowerCase() === "paid") return s + safeNum(rr.amount || rr.amount_paid);
          return s;
        }, 0);

        const scholarship = rowsY.reduce((s, rr) => {
          if (rr.status && String(rr.status).toLowerCase() === "refunded") return s;
          return s + safeNum(rr.scholarship_amount);
        }, 0);

        const refunds = rowsY.reduce((s, rr) => {
          const isRefund = looksLikeRefund(rr);
          if (isRefund) {
            const cand = Math.abs(safeNum(rr.scholarship_amount) || safeNum(rr.amount_paid) || safeNum(rr.amount) || 0);
            return s + cand;
          }
          const negCheck = Math.max(0, -Math.min(safeNum(rr.amount), safeNum(rr.amount_paid), safeNum(rr.scholarship_amount)));
          return s + negCheck;
        }, 0);

        const receivedY = Number((tuitionPaid + scholarship).toFixed(2));
        const appliedY = Number((receivedY + carryIn).toFixed(2));
        const annualY = Number(annual[y] || 0);
        let pendingY = Math.max(annualY - appliedY, 0);

        const nextYear = y + 1;
        const nextYearHasCarryFromThis = g.rows.some((rr) => {
          const isCarry = String(rr.mode || "").toLowerCase().includes("carryforward");
          return clampYear(rr.course_year) === nextYear && isCarry;
        });
        if ((y === 1 || y === 2) && nextYearHasCarryFromThis && appliedY > annualY) pendingY = 0;

        const excessY = Number(Math.max(receivedY - annualY, 0).toFixed(2));
        const refundedY = Number(refunds.toFixed(2));

        perYear[y] = { received: receivedY, carryIn: Number(carryIn.toFixed(2)), applied: appliedY, pending: Number(pendingY.toFixed(2)), excess: excessY, refunded: refundedY };

        totalReceived += receivedY; totalCarryIn += carryIn; totalApplied += appliedY; totalPending += pendingY; totalExcess += excessY; totalRefunded += refundedY;
      }

      out.push({
        key: g.key,
        student_id: rep.student_id ?? null,
        name: displayName,
        category: displayCategory,
        perYear,
        totals: {
          received: Number(totalReceived.toFixed(2)),
          carryIn: Number(totalCarryIn.toFixed(2)),
          applied: Number(totalApplied.toFixed(2)),
          pending: Number(totalPending.toFixed(2)),
          excess: Number(totalExcess.toFixed(2)),
          refunded: Number(totalRefunded.toFixed(2)),
        },
      });
    }

    out.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
    return out;
  }, [feeRows, selectedBatch, search, batchAnnual, resolveStudentForRow]);

  const totalsByCategory = useMemo(() => {
    const map = {};
    studentsAggregated.forEach((s) => {
      const cat = s.category || "N/A";
      if (!map[cat]) map[cat] = { pending: 0, excess: 0, refunded: 0, count: 0, applied: 0 };
      map[cat].pending += s.totals.pending;
      map[cat].excess += s.totals.excess;
      map[cat].refunded += s.totals.refunded;
      map[cat].applied += s.totals.applied;
      map[cat].count += 1;
    });
    return map;
  }, [studentsAggregated]);

  const overallTotals = useMemo(() => {
    return studentsAggregated.reduce((acc, s) => {
      acc.pending += s.totals.pending;
      acc.excess += s.totals.excess;
      acc.refunded += s.totals.refunded;
      acc.applied += s.totals.applied;
      acc.count += 1;
      return acc;
    }, { pending:0, excess:0, refunded:0, applied:0, count:0 });
  }, [studentsAggregated]);

  // CSV export
  const exportCSV = useCallback(() => {
    const hdr = [
      "Name","Category",
      "Y1 Received","Y1 CarryIn","Y1 Pending","Y1 Excess","Y1 Refund (₹)",
      "Y2 Received","Y2 CarryIn","Y2 Pending","Y2 Excess","Y2 Refund (₹)",
      "Y3 Received","Y3 CarryIn","Y3 Pending","Y3 Excess","Y3 Refund (₹)",
      "Total Received","Total CarryIn","Total Applied","Total Pending","Total Excess","Total Refunded"
    ];
    const lines = [hdr.join(",")];
    studentsAggregated.forEach((s) => {
      lines.push([
        `"${(s.name||"").replace(/"/g,'""')}"`,
        `"${(s.category||"").replace(/"/g,'""')}"`,
        s.perYear[1].received.toFixed(2), s.perYear[1].carryIn.toFixed(2), s.perYear[1].pending.toFixed(2), s.perYear[1].excess.toFixed(2), s.perYear[1].refunded.toFixed(2),
        s.perYear[2].received.toFixed(2), s.perYear[2].carryIn.toFixed(2), s.perYear[2].pending.toFixed(2), s.perYear[2].excess.toFixed(2), s.perYear[2].refunded.toFixed(2),
        s.perYear[3].received.toFixed(2), s.perYear[3].carryIn.toFixed(2), s.perYear[3].pending.toFixed(2), s.perYear[3].excess.toFixed(2), s.perYear[3].refunded.toFixed(2),
        s.totals.received.toFixed(2), s.totals.carryIn.toFixed(2), (s.totals.applied || (s.totals.received + s.totals.carryIn)).toFixed(2), s.totals.pending.toFixed(2), s.totals.excess.toFixed(2), s.totals.refunded.toFixed(2)
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fee_report_${selectedBatch ? `batch_${selectedBatch}` : "all"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [studentsAggregated, selectedBatch]);

  // helper to capture page style nodes
  const getPageStylesHtml = useCallback(() => {
    const nodes = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'));
    return nodes.map((n) => n.outerHTML).join("\n");
  }, []);

  // Print (unchanged)
  const handlePrint = useCallback(() => {
    if (!printRootRef.current) return alert("Nothing to print");
    const printableHtml = printRootRef.current.innerHTML;
    const w = window.open("", "_blank");
    if (!w) return alert("Unable to open print window (popup blocked). Allow popups or use Ctrl+P.");
    const pageStyles = getPageStylesHtml();
    const overrideCss = `
      <style>
        @page { size: A2 landscape; margin: 8mm; }
        html,body{margin:0;padding:0;height:100%; background:#fff;}
        body{font-family: Arial, Helvetica, sans-serif; color:#222; -webkit-print-color-adjust:exact;}
        .print-root{padding:10px; box-sizing:border-box; width:100%;}
        table{width:100%; border-collapse:collapse; table-layout:fixed; font-size:10px;}
        th, td { border:1px solid #ddd; padding:4px 6px; vertical-align:middle; word-break:break-word; white-space: nowrap; }
        th{background:#f3f8ff; color:#184e77; font-weight:700; font-size:9px;}
        .fee-category-col { width: 56px; max-width:56px; text-align:center; padding-left:4px; padding-right:4px; }
        thead { display: table-header-group; }
        tfoot { display: table-footer-group; }
        tr { page-break-inside: avoid; }
        .controls, .actions, .compactToggle, .fee-action-btn { display: none !important; }
        .pdf-batch-heading { font-size: 16px; font-weight: 800; color: #0b3b5b; margin-bottom: 12px; display:block; }
      </style>
    `;
    const html = `<!doctype html>
      <html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/><title>Fee Report - Print</title>${pageStyles}${overrideCss}</head><body><div id="print-root" class="print-root">${printableHtml}</div></body></html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 300);
  }, [getPageStylesHtml]);

  // Export PDF: draw Batch text onto the captured canvas so it appears with the content
  const exportPDF = useCallback(async () => {
    const el = document.getElementById("fee-report-print-root");
    if (!el) return alert("Report content not available for PDF export.");

    const loadScript = (src) => new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) return resolve();
      const s = document.createElement("script");
      s.src = src;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error("Failed to load " + src));
      document.head.appendChild(s);
    });

    let container = null;
    try {
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
    } catch (e) {
      console.error(e);
      return alert("Failed to load PDF libraries. Use Print -> Save as PDF instead.");
    }

    const html2canvas = window.html2canvas;
    const { jsPDF } = window.jspdf || window.jspdf || {};
    if (!html2canvas || !jsPDF) return alert("PDF libraries not available. Use Print -> Save as PDF.");

    // Clone DOM and inject inline styles (we will capture the report area)
    const clone = el.cloneNode(true);

    // Remove duplicates of any prior headings if present
    try {
      const existing = clone.querySelectorAll(".pdf-batch-heading, #pdf-batch-heading");
      existing.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
    } catch (err) { /* ignore */ }

    // Inline styles for clone to make capture consistent
    const inlineStyle = document.createElement("style");
    inlineStyle.innerHTML = `
      table { table-layout: fixed !important; width: 100% !important; word-break: break-word !important; font-size: 9px !important; }
      th, td { padding: 4px 6px !important; border: 1px solid #ddd !important; vertical-align: middle !important; }
      th { background: #f3f8ff !important; color: #184e77 !important; font-weight: 700 !important; font-size: 8px !important; }
      .fee-category-col { width: 56px !important; max-width:56px !important; text-align:center !important; padding-left:4px !important; padding-right:4px !important; }
      td.numeric { white-space: nowrap !important; }
      .student-name-cell { white-space: normal !important; word-break: break-word !important; max-width: 260px !important; }
      .report-grid { display: block !important; }
      .report-grid > * { width: 100% !important; max-width: 100% !important; float: none !important; display: block !important; }
      .category-grid { display: grid !important; grid-template-columns: repeat(auto-fit, minmax(220px, 360px)) !important; gap: 12px !important; justify-content: start !important; }
      .category-kpi { background: #fff !important; border: 1px solid #eef3f8 !important; border-radius: 8px !important; padding: 12px !important; display:flex !important; justify-content:space-between !important; align-items:center !important; max-width: 360px !important; }
      .kpi-card, .summary, .category-kpi { font-size: 11px !important; }
    `;
    clone.insertBefore(inlineStyle, clone.firstChild);

    // Off-screen container for clone
    container = document.createElement("div");
    container.style.position = "fixed";
    container.style.left = "-10000px";
    container.style.top = "0";
    container.style.zIndex = "9999";
    container.appendChild(clone);
    document.body.appendChild(container);

    try {
      // A2 width in mm: 594; convert to CSS px (approx 96dpi)
      const A2_WIDTH_MM = 594;
      const mmToPx = (mm) => Math.round((mm * 96) / 25.4);
      const A2_CSS_PX = mmToPx(A2_WIDTH_MM);

      clone.style.boxSizing = "border-box";
      clone.style.width = `${A2_CSS_PX}px`;
      clone.style.maxWidth = `${A2_CSS_PX}px`;
      clone.style.background = "#ffffff";
      clone.style.padding = "8px";

      // Force layout and give browser some time to compute styles
      await new Promise((r) => setTimeout(r, 250));
      if (typeof clone.scrollTop === "number") clone.scrollTop = 0;

      const dpr = Math.max(1, window.devicePixelRatio || 1);
      const scale = Math.min(3, dpr * 1.4);

      // Capture the report area (as a single tall canvas)
      const capturedCanvas = await html2canvas(clone, {
        scale,
        useCORS: true,
        allowTaint: true,
        logging: false,
        windowWidth: A2_CSS_PX,
        windowHeight: clone.scrollHeight,
      });

      // Draw the batch heading onto a new combined canvas above the captured content
      // Compute heading height in pixels (scale-aware)
      const headingFontPx = Math.max(16, Math.round(18 * (scale / dpr))); // visual-ish size
      const headingPaddingPx = Math.round(12 * (scale / dpr));
      const headingHeightPx = headingFontPx + headingPaddingPx * 2;

      const combinedCanvas = document.createElement("canvas");
      combinedCanvas.width = capturedCanvas.width;
      combinedCanvas.height = capturedCanvas.height + headingHeightPx;
      const ctx = combinedCanvas.getContext("2d");

      // Fill background white
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);

      // Draw the heading text
      ctx.fillStyle = "#0b3b5b";
      // choose font using px size relative to canvas pixel scale
      ctx.font = `${headingFontPx}px Helvetica, Arial, sans-serif`;
      ctx.textBaseline = "middle";
      const leftPaddingPx = Math.round(16 * (scale / dpr));
      const textY = Math.round(headingHeightPx / 2);
      ctx.fillText(`Batch: ${batchDisplayName}`, leftPaddingPx, textY);

      // Draw the captured report below the heading
      ctx.drawImage(capturedCanvas, 0, headingHeightPx);

      // Create PDF and slice the combinedCanvas into A2 pages
      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF({ unit: "mm", format: [A2_WIDTH_MM, 420] });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 8; // mm margin for content

      const canvasWidthPx = combinedCanvas.width;
      const canvasHeightPx = combinedCanvas.height;
      const imgWidthMM = pdfWidth - margin * 2;
      const imgHeightMM = (canvasHeightPx / canvasWidthPx) * imgWidthMM;

      // Slice vertically into pages (simple approach; no header repeat)
      const pxPerMm = canvasHeightPx / imgHeightMM;
      const availablePxPerPage = Math.floor((pdfHeight - margin * 2) * pxPerMm);

      let remaining = canvasHeightPx;
      let offset = 0;
      let firstSlice = true;
      while (remaining > 0) {
        const slicePx = Math.min(remaining, availablePxPerPage);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = combinedCanvas.width;
        sliceCanvas.height = slicePx;
        const sliceCtx = sliceCanvas.getContext("2d");
        sliceCtx.drawImage(combinedCanvas, 0, offset, combinedCanvas.width, slicePx, 0, 0, combinedCanvas.width, slicePx);
        const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.95);
        const sliceMM = slicePx / pxPerMm;

        if (firstSlice) {
          // first page already exists in jsPDF by default; replace content by adding the image on it
          pdf.addImage(sliceData, "JPEG", margin, margin, imgWidthMM, sliceMM);
          firstSlice = false;
        } else {
          pdf.addPage();
          pdf.addImage(sliceData, "JPEG", margin, margin, imgWidthMM, sliceMM);
        }

        remaining -= slicePx;
        offset += slicePx;
      }

      const fileName = `fee_report_A2_${new Date().toISOString().slice(0,10)}.pdf`;
      pdf.save(fileName);
    } catch (e) {
      console.error("PDF export error:", e);
      alert("Failed to generate PDF. Try Print -> Save as PDF as fallback.");
    } finally {
      try {
        if (container && container.parentNode) container.parentNode.removeChild(container);
      } catch (err) { /* ignore cleanup errors */ }
    }
  }, [batchDisplayName]);

  // UI styles & render (unchanged)
  const uiStyles = {
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 },
    kpiCardContainer: { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 },
    kpiCard: { flex: "1 1 180px", background: "#fff", border: "1px solid #e6eef8", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(20,40,80,0.04)" },
    actions: { display: "flex", gap: 8, alignItems: "center" },
    tableWrap: { overflowX: "auto", borderRadius: 8, border: "1px solid #eef3f8" },
    compactToggle: { marginLeft: 8, cursor: "pointer", background: "#f0f6fb", border: "none", padding: "6px 8px", borderRadius: 6 },
    reportGrid: { display: "grid", gridTemplateColumns: "1fr 320px", gap: 16, alignItems: "start" },
    errorBanner: { background: "#ffecea", color: "#c62828", padding: "8px 12px", borderRadius: 6, marginBottom: 12, border: "1px solid #f5c6cb" }
  };

  return (
    <div style={{ padding: 16, fontFamily: "Inter, Roboto, Arial, Helvetica, sans-serif" }}>
      <div style={uiStyles.header}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#0b3b5b" }}>Institute Fee Report</div>
          <div style={{ color: "#557a95", marginTop: 4 }}>
            {selectedBatch ? (batches.find(bb => String(bb.id) === String(selectedBatch))?.name || selectedBatch) : "All Batches"} — Year-wise
          </div>
        </div>

        <div style={uiStyles.actions} className="actions">
          <div style={{ marginRight: 12 }}>
            <label style={{ fontSize: 12, color: "#34495e", marginRight: 8 }}>Batch</label>
            <select value={selectedBatch} onChange={e => setSelectedBatch(e.target.value)} style={{ padding: "6px 8px", borderRadius: 6 }}>
              <option value="">All Batches</option>
              {batches.map((b) => <option key={String(b.id)} value={String(b.id)}>{b.name}</option>)}
            </select>
          </div>

          <div style={{ marginRight: 12 }} className="controls">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by student, category..." style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #d7e6f4", width: 240 }} />
          </div>

          <div>
            <button onClick={exportCSV} disabled={!studentsAggregated.length || loading} className="fee-action-btn" style={{ background: "#0b67b2", color: "#fff", padding: "8px 12px", border: "none", borderRadius: 8 }}>Export CSV</button>
            <button onClick={handlePrint} disabled={!studentsAggregated.length || loading} className="fee-action-btn" style={{ marginLeft: 8, background: "#0b67b2", color: "#fff", padding: "8px 12px", border: "none", borderRadius: 8 }}>Print</button>
            <button onClick={exportPDF} disabled={!studentsAggregated.length || loading} className="fee-action-btn" style={{ marginLeft: 8, background: "#0b67b2", color: "#fff", padding: "8px 12px", border: "none", borderRadius: 8 }}>Export A2 PDF</button>
            <button onClick={() => setCompact(!compact)} className="compactToggle" style={uiStyles.compactToggle}>{compact ? "Normal view" : "Compact view"}</button>
          </div>
        </div>
      </div>

      {error && (<div style={uiStyles.errorBanner}><strong>Error:</strong> {error} <button onClick={() => setError("")} style={{ marginLeft: 12, background: "transparent", border: "none", color: "#c62828" }}>Dismiss</button></div>)}
      {loading && (<div style={{ marginBottom: 12, color: "#34495e" }}>Loading data... Please wait.</div>)}

      <div ref={printRootRef} id="fee-report-print-root">
        <div className="pdf-batch-heading">Batch: {batchDisplayName}</div>

        <div style={uiStyles.kpiCardContainer}>
          <div className="kpi-card" style={uiStyles.kpiCard}><div style={{ fontSize: 12, color: "#557a95" }}>Applied Total</div><div style={{ fontSize: 20, fontWeight: 700, color: "#0b3b5b" }}>{formatCurrency(overallTotals.applied)}</div></div>
          <div className="kpi-card" style={uiStyles.kpiCard}><div style={{ fontSize: 12, color: "#557a95" }}>Pending</div><div style={{ fontSize: 20, fontWeight: 700, color: "#c62828" }}>{formatCurrency(overallTotals.pending)}</div></div>
          <div className="kpi-card" style={uiStyles.kpiCard}><div style={{ fontSize: 12, color: "#557a95" }}>Excess</div><div style={{ fontSize: 20, fontWeight: 700, color: "#f57c00" }}>{formatCurrency(overallTotals.excess)}</div></div>
          <div className="kpi-card" style={uiStyles.kpiCard}><div style={{ fontSize: 12, color: "#557a95" }}>Refunded</div><div style={{ fontSize: 20, fontWeight: 700, color: "#2e7d32" }}>{formatCurrency(overallTotals.refunded)}</div></div>
        </div>

        <div className="report-grid" style={uiStyles.reportGrid}>
          <div style={{ flex: 1 }}>
            <div style={uiStyles.tableWrap}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: compact ? 10 : 11 }}>
                <thead>
                  <tr>
                    <th rowSpan={2} style={{ textAlign: "left", padding: 8 }}>Student Name</th>
                    <th rowSpan={2} className="fee-category-col" style={{ width: 56, padding: 8 }}>Category</th>

                    <th colSpan={5} style={{ padding: 8 }}>Year 1</th>
                    <th colSpan={5} style={{ padding: 8 }}>Year 2</th>
                    <th colSpan={5} style={{ padding: 8 }}>Year 3</th>

                    <th colSpan={4} style={{ padding: 8 }}>Totals</th>
                  </tr>
                  <tr>
                    <th style={{ padding: 8 }}>Received</th><th style={{ padding: 8 }}>CarryIn</th><th style={{ padding: 8 }}>Pending</th><th style={{ padding: 8 }}>Excess</th><th style={{ padding: 8 }}>Refund</th>

                    <th style={{ padding: 8 }}>Received</th><th style={{ padding: 8 }}>CarryIn</th><th style={{ padding: 8 }}>Pending</th><th style={{ padding: 8 }}>Excess</th><th style={{ padding: 8 }}>Refund</th>

                    <th style={{ padding: 8 }}>Received</th><th style={{ padding: 8 }}>CarryIn</th><th style={{ padding: 8 }}>Pending</th><th style={{ padding: 8 }}>Excess</th><th style={{ padding: 8 }}>Refund</th>

                    <th style={{ padding: 8 }}>Received</th><th style={{ padding: 8 }}>CarryIn</th><th style={{ padding: 8 }}>Pending</th><th style={{ padding: 8 }}>Excess</th>
                  </tr>
                </thead>

                <tbody>
                  {studentsAggregated.length === 0 ? (
                    <tr><td colSpan={24} style={{ padding: 12, textAlign: "center" }}>No students found.</td></tr>
                  ) : studentsAggregated.map((s, idx) => {
                    const pendingAmount = Number(s.totals.pending || 0);
                    const pendingStyle = pendingAmount > 0 ? { color: "#c62828", fontWeight: 700 } : { color: "#2e7d32", fontWeight: 700 };
                    return (
                      <tr key={s.key} style={{ background: idx % 2 === 0 ? "#ffffff" : "#fbfdff" }}>
                        <td className="student-name-cell" style={{ padding: 8, overflow: "hidden", textOverflow: "ellipsis", maxWidth: 260 }}>{s.name}</td>
                        <td className="fee-category-col" style={{ padding: 6, textAlign: "center" }}>{s.category}</td>

                        <td className="numeric" style={{ padding: 8, textAlign: "right" }}>{formatCurrency(s.perYear[1].received)}</td>
                        <td className="numeric" style={{ padding: 8, textAlign: "right" }}>{formatCurrency(s.perYear[1].carryIn)}</td>
                        <td className="numeric" style={{ padding: 8, textAlign: "right", ...pendingStyle }}>{formatCurrency(s.perYear[1].pending)}</td>
                        <td className="numeric" style={{ padding: 8, textAlign: "right", color: "#f57c00", fontWeight: 700 }}>{formatCurrency(s.perYear[1].excess)}</td>
                        <td className="numeric" style={{ padding: 8, textAlign: "right" }}>{formatCurrency(s.perYear[1].refunded)}</td>

                        <td className="numeric" style={{ padding: 8, textAlign: "right" }}>{formatCurrency(s.perYear[2].received)}</td>
                        <td className="numeric" style={{ padding: 8, textAlign: "right" }}>{formatCurrency(s.perYear[2].carryIn)}</td>
                        <td className="numeric" style={{ padding: 8, textAlign: "right", ...pendingStyle }}>{formatCurrency(s.perYear[2].pending)}</td>
                        <td className="numeric" style={{ padding: 8, textAlign: "right", color: "#f57c00", fontWeight: 700 }}>{formatCurrency(s.perYear[2].excess)}</td>
                        <td className="numeric" style={{ padding: 8, textAlign: "right" }}>{formatCurrency(s.perYear[2].refunded)}</td>

                        <td className="numeric" style={{ padding: 8, textAlign: "right" }}>{formatCurrency(s.perYear[3].received)}</td>
                        <td className="numeric" style={{ padding: 8, textAlign: "right" }}>{formatCurrency(s.perYear[3].carryIn)}</td>
                        <td className="numeric" style={{ padding: 8, textAlign: "right", ...pendingStyle }}>{formatCurrency(s.perYear[3].pending)}</td>
                        <td className="numeric" style={{ padding: 8, textAlign: "right", color: "#f57c00", fontWeight: 700 }}>{formatCurrency(s.perYear[3].excess)}</td>
                        <td className="numeric" style={{ padding: 8, textAlign: "right" }}>{formatCurrency(s.perYear[3].refunded)}</td>

                        <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 700 }}>{formatCurrency(s.totals.received)}</td>
                        <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 700 }}>{formatCurrency(s.totals.carryIn)}</td>
                        <td className="numeric" style={{ padding: 8, textAlign: "right", ...pendingStyle }}>{formatCurrency(s.totals.pending)}</td>
                        <td className="numeric" style={{ padding: 8, textAlign: "right", color: "#f57c00", fontWeight: 800 }}>{formatCurrency(s.totals.excess)}</td>
                      </tr>
                    );
                  })}
                </tbody>

                {studentsAggregated.length > 0 && (
                  <tfoot>
                    <tr style={{ background: "#f2f8ff" }}>
                      <td colSpan={2} style={{ padding: 8, fontWeight: 800 }}>Totals</td>

                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 700 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.perYear[1].received, 0))}</td>
                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 700 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.perYear[1].carryIn, 0))}</td>
                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 700 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.perYear[1].pending, 0))}</td>
                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 700 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.perYear[1].excess, 0))}</td>
                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 700 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.perYear[1].refunded, 0))}</td>

                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 700 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.perYear[2].received, 0))}</td>
                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 700 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.perYear[2].carryIn, 0))}</td>
                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 700 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.perYear[2].pending, 0))}</td>
                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 700 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.perYear[2].excess, 0))}</td>
                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 700 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.perYear[2].refunded, 0))}</td>

                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 700 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.perYear[3].received, 0))}</td>
                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 700 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.perYear[3].carryIn, 0))}</td>
                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 700 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.perYear[3].pending, 0))}</td>
                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 700 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.perYear[3].excess, 0))}</td>
                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 700 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.perYear[3].refunded, 0))}</td>

                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 800 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.totals.received, 0))}</td>
                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 800 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.totals.carryIn, 0))}</td>
                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 800 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.totals.pending, 0))}</td>
                      <td className="numeric" style={{ padding: 8, textAlign: "right", fontWeight: 800 }}>{formatCurrency(studentsAggregated.reduce((a, s) => a + s.totals.excess, 0))}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Bottom KPI & summary */}
          <div style={{ width: 320 }}>
            <div style={{ marginBottom: 12 }}>
              <div className="summary" style={uiStyles.kpiCard}>
                <div style={{ fontSize: 12, color: "#557a95" }}>Applied Total</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{formatCurrency(overallTotals.applied)}</div>
              </div>

              <div style={{ height: 12 }} />

              {/* Category KPI grid */}
              <div style={{ background: "#fff", border: "1px solid #eef3f8", borderRadius: 8, padding: 12 }}>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>Summary by Category (Bottom)</div>

                {Object.keys(totalsByCategory).length === 0 ? (
                  <div className="small">No category data</div>
                ) : (
                  <div className="category-grid" style={{ marginTop: 8 }}>
                    {Object.entries(totalsByCategory).map(([cat, t]) => (
                      <div key={cat} className="category-kpi">
                        <div>
                          <div style={{ fontWeight: 700 }}>{cat}</div>
                          <div style={{ fontSize: 12, color: "#6b7f8f" }}>{t.count} students</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontWeight: 800, fontSize: 16 }}>{formatCurrency(t.pending)}</div>
                          <div style={{ fontSize: 12, color: "#6b7f8f" }}>Pending</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ marginTop: 12, paddingTop: 8, borderTop: "1px solid #eef3f8", fontWeight: 700 }}>
                  Totals: {overallTotals.count} students — Pending {formatCurrency(overallTotals.pending)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}